import { TableTheme } from "@omegagrid/grid";
import { AxisAggregation, AxisIndexItem, AxisResult, DataResult, IndicatorResult, ReportComment, ResultSpecs } from "../types";
import { renderIndicatorLabel } from "../components/visualizations/helpers";
import { findColumnIndex } from "../common";
import { msg } from "@omegagrid/localize";

const KEY_SEPARATOR = '_';

export class ReportResult {

	public readonly xAxis: AxisResult[] = [];
	public readonly xAggregations: Record<string, AxisAggregation> = {};
	public readonly yAxis: AxisResult[] = [];
	public readonly yAggregations: Record<string, AxisAggregation> = {};
	public readonly indicators: IndicatorResult[] = [];

	private _indicatorAxis: AxisResult;
	get indicatorAxis() { return this._indicatorAxis ?? ( this._indicatorAxis =
		this.yAxis.find(axis => axis.axisType == 'indicators') ||
		this.xAxis.find(axis => axis.axisType == 'indicators')
	) }

	private _data: DataResult;
	public get data() { return this._data }

	private _theme: TableTheme;
	/** table-wide theme resolved by the server (view_options.theme), from the 'theme' result set */
	public get theme(): TableTheme|undefined { return this._theme }

	private _comments: ReportComment[] = [];
	/** cell comments of the report (P_COMMENT prefiltered by module + filter hash), from the 'comments' result set */
	public get comments(): ReportComment[] { return this._comments }

	public readonly dataIndex = new Map<string, Record<string, number>>();
	public readonly xIndex: AxisIndexItem[] = [];
	public readonly yIndex: AxisIndexItem[] = []

	get headerCount() {
		return this.xAxis.reduce((acc, axis) => acc + (/*axis.levelCount ||*/ 1), 0);
	}

	private _yParentBasesAxis = -1;
	get yParentBasesAxis() { return this._yParentBasesAxis }

	private _yLevels = 0;
	get yLevels() { return this._yLevels }

	private _xLevels = 0;
	get xLevels() { return this._xLevels }

	get isEmpty() {
		return !this.data?.data.length;
	}

	/** display null values as zeros (D_STATEMENT.NULLS_TO_ZEROS, carried on the statement axis) */
	get nullsToZeros() {
		return [...this.xAxis, ...this.yAxis].some(axis => axis.emptyItems == 'zeros');
	}

	private indexAxisResult(axisResult: AxisResult) {
		axisResult.columnIndex = {
			idz: findColumnIndex(axisResult.columns, 'IDZ'),
			id: findColumnIndex(axisResult.columns, 'ID_DIM'),
			key: findColumnIndex(axisResult.columns, 'KEY'),
			dim: findColumnIndex(axisResult.columns, 'DIM'),
			level: findColumnIndex(axisResult.columns, 'LEVEL'),
			parent: findColumnIndex(axisResult.columns, 'PARENT'),
			indent: findColumnIndex(axisResult.columns, 'INDENT'),
			style: findColumnIndex(axisResult.columns, 'STYLE'),
			format: findColumnIndex(axisResult.columns, 'FORMAT'),
			drill: findColumnIndex(axisResult.columns, 'DRILL'),
			hidden: findColumnIndex(axisResult.columns, 'HIDDEN'),
			collapsed: findColumnIndex(axisResult.columns, 'COLLAPSED'),
			view_options: findColumnIndex(axisResult.columns, 'VIEW_OPTIONS'),
			indicator_dim: findColumnIndex(axisResult.columns, 'INDICATOR_DIM')
		};

		const index = new Map<number, number>();
		const idzIndex = axisResult.columnIndex.idz;
		const levelIndex = axisResult.columnIndex.level;
		axisResult.data.forEach((row, i) => {
			index.set(row[idzIndex], i);
			axisResult.levelCount = Math.max(axisResult.levelCount || 0, parseInt(row[levelIndex]) + 1);

			if (axisResult.columnIndex.view_options != null) {
				try {
					row[axisResult.columnIndex.view_options] = JSON.parse(row[axisResult.columnIndex.view_options] || '{}');
				} catch (e) {
					console.warn(e);
				}
			}

			if (axisResult.columnIndex.indicator_dim > -1) {
				try {
					row[axisResult.columnIndex.indicator_dim] = JSON.parse(row[axisResult.columnIndex.indicator_dim] || 'null');
				} catch (e) {
					console.warn(e);
				}
			}
		});
		axisResult.index = index;
		return index;
	}

	private indexIndicators(axisResult: AxisResult) {
		const dataColumnNameIndex = findColumnIndex(axisResult.columns, 'DATA_COLUMN');
		const unitDataColumnIndex = findColumnIndex(axisResult.columns, 'UNIT_DATA_COLUMN');
		const nameColumnIndex = findColumnIndex(axisResult.columns, 'NAME');
		const formatColumnIndex = findColumnIndex(axisResult.columns, 'FORMAT');
		const descriptionColumnIndex = findColumnIndex(axisResult.columns, 'DESCRIPTION');
		const filterColumnIndex = findColumnIndex(axisResult.columns, 'FILTERS');
		const rendererColumnIndex = findColumnIndex(axisResult.columns, 'RENDERER');
		const displayUnitColumnIndex = findColumnIndex(axisResult.columns, 'DISPLAY_UNIT');
		const keyColumnIndex = findColumnIndex(axisResult.columns, 'KEY');

		axisResult.columnIndex = {
			id: findColumnIndex(axisResult.columns, 'DATA_COLUMN'),
			dim: findColumnIndex(axisResult.columns, 'NAME'),
			level: -1,
			indent: -1,
		};

		axisResult.data.forEach((row, i) => this.indicators.push({
			index: i,
			name: msg(row[nameColumnIndex]),
			key: row[keyColumnIndex],
			format: row[formatColumnIndex],
			dataColumn: row[dataColumnNameIndex],
			unitDataColumn: row[unitDataColumnIndex],
			dataColumnIndex: null, //findColumnIndex(axisResult.columns, row[dataColumnNameIndex])
			description: descriptionColumnIndex != -1 ? row[descriptionColumnIndex] : undefined,
			filters: filterColumnIndex != -1 && row[filterColumnIndex] ? JSON.parse(row[filterColumnIndex]) : undefined,
			renderer: rendererColumnIndex != -1 ? row[rendererColumnIndex] : undefined,
			displayUnit: displayUnitColumnIndex != -1 ? row[displayUnitColumnIndex] || 1 : 1,
		}));

		this.indicators.forEach(i => i.label = renderIndicatorLabel(i));
	}

	private createKey(columns: [number, number][], dataRow: string[], indicatorIndex: number) {
		if (columns.length == 0) return null;
		return columns.map(col => col[0] == -1 ? 'I' + indicatorIndex.toString() : (dataRow[col[0]] == '' ? '' : ('D' + dataRow[col[0]]))).join(KEY_SEPARATOR);
	}

	private indexDataResult(dataResult: DataResult) {
		const indexedXAxisColumnNames = this.xAxis.map((axis, level) => [axis.dataColumn, level]); //.filter(col => col[0] != null);
		const indexedYAxisColumnNames = this.yAxis.map((axis, level) => [axis.dataColumn, level]); //.filter(col => col[0] != null);

		const xAxisLevelCount = this.xAxis.length;
		const xLevelOffsets = new Array(xAxisLevelCount).fill(0);

		for (let i = 1; i < xAxisLevelCount; i++) {
			xLevelOffsets[i] = xLevelOffsets[i - 1] + (this.xAxis[i - 1].levelCount || 0);
		}

		const yAxisLevelCount = this.yAxis.length;
		const yLevelOffsets = new Array(yAxisLevelCount).fill(0);

		for (let i = 1; i < yAxisLevelCount; i++) {
			yLevelOffsets[i] = yLevelOffsets[i - 1] + (this.yAxis[i - 1].levelCount || 0);
		}

		const indexedXAxisColumns = indexedXAxisColumnNames.map(
			axisCol => [findColumnIndex(dataResult.columns, axisCol[0] as string), axisCol[1]]
		) as [number, number][];

		const indexedYAxisColumns = indexedYAxisColumnNames.map(
			axisCol => [dataResult.columns.findIndex(col => col.name == axisCol[0]), axisCol[1]]
		) as [number, number][];

		const xKeyIndexMap = new Map<string, number>();
		const yKeyIndexMap = new Map<string, number>();

		let indexes: number[], xKey: string, yKey: string, axisColumnItem: [number, number];
		let axisLevel: number, itemLevel: number, index: number, parentIndex: number, value: string;
		let axisResult: AxisResult, indexItem: AxisIndexItem;

		const indicatorAxis = this.indicatorAxis;
		const indicators = this.indicators;
		const indicatorAxisIndex = indicatorAxis.axisIndex || 0;

		// const xIndexMap = new Map<number, Map<number, number>>(this.xAxis.map((_, i) => [i, new Map<number, number>()]));
		// const yIndexMap = new Map<number, Map<number, number>>(this.yAxis.map((_, i) => [i, new Map<number, number>()]));

		for (let dataIndex = 0; dataIndex < dataResult.data.length; dataIndex++) {
			const dataRow = dataResult.data[dataIndex];

			for (let j = 0; j < indicators.length; j++) {
				// y-axis
				// only first iteration if indicator is not on y-axis
				if (j == 0 || indicatorAxisIndex == 1) {
					yKey = this.createKey(indexedYAxisColumns, dataRow, j);
					indexes = new Array(indexedYAxisColumns.length);
					axisLevel = -1;
					itemLevel = 0;
					parentIndex = null;

					for (let i = 0; i < indexedYAxisColumns.length; i++) {
						axisColumnItem = indexedYAxisColumns[i];
						axisResult = this.yAxis[axisColumnItem[1]];

						if (axisResult.index) { // dimension
							value = dataRow[axisColumnItem[0]];
							index = axisResult.index.get(dataRow[axisColumnItem[0]]);
							indexes[i] = index ?? -1;

							if (value !== '') {
								++axisLevel;

								parentIndex = index != null && axisResult.columnIndex.parent > -1
									? parseInt(axisResult.data[index][axisResult.columnIndex.parent])
									: null

								if (parentIndex != null && parentIndex > -1) this._yParentBasesAxis = axisLevel;
								else parentIndex = null;

								if (index != null) {
									itemLevel += parseInt(axisResult.data[index][axisResult.columnIndex.level]);
								}
							}
						} else { // indicator
							indexes[i] = j;
							++axisLevel;
							++itemLevel;
						}
					}

					if (yKey != null && !yKeyIndexMap.has(yKey)) {
						yKeyIndexMap.set(yKey, this.yIndex.length);

						indexItem = [
							yKey,
							indexes,
							axisLevel,
							isNaN(itemLevel) ? null : (itemLevel + (yLevelOffsets[axisLevel] || 0)),
							parentIndex
						];

						this._yLevels = Math.max(this._yLevels, indexItem[3]);
						// yIndexMap.get(axisLevel).set(indexes[axisLevel], this.yIndex.length);
						this.yIndex.push(indexItem);
					}
				}

				// x-axis
				// only first iteration if indicator is not on x-axis
				if (j == 0 || indicatorAxisIndex == 0) {
					xKey = this.createKey(indexedXAxisColumns, dataRow, j);
					indexes = new Array(indexedXAxisColumns.length);
					axisLevel = -1;
					itemLevel = 0;

					for (let i = 0; i < indexedXAxisColumns.length; i++) {
						axisColumnItem = indexedXAxisColumns[i];
						axisResult = this.xAxis[axisColumnItem[1]];

						if (axisResult.index) { // dimension
							value = dataRow[axisColumnItem[0]];
							index = axisResult.index.get(dataRow[axisColumnItem[0]]);
							indexes[i] = index;
							if (value !== '') {
								++axisLevel;
								if (index != null) {
									itemLevel += parseInt(axisResult.data[index][axisResult.columnIndex.level]);
								}
							}
						} else { // indicator
							indexes[i] = j;
							++axisLevel;
							++itemLevel;
						}
					}

					if (xKey != null && !xKeyIndexMap.has(xKey)) {
						xKeyIndexMap.set(xKey, this.xIndex.length);
						indexItem = [xKey, indexes, axisLevel, itemLevel + xLevelOffsets[axisLevel]];
						this._xLevels = Math.max(this._xLevels, indexItem[3]);
						this.xIndex.push(indexItem);
					}
				}

				let row = this.dataIndex.get(yKey);
				if (!row) {
					row = {};
					this.dataIndex.set(yKey, row);
				}
				row[xKey] = dataIndex;
			}
		};

		this.fillMissingAxisItems(this.yAxis, this.yIndex, yLevelOffsets, true);
		this.fillMissingAxisItems(this.xAxis, this.xIndex, xLevelOffsets, false);

		this.yIndex.sort((a, b) => {
			for (let i = 0; i < a[1].length; i++) {
				if (a[1][i] != b[1][i]) return a[1][i] - b[1][i];
			}
			return 0;
		});

		this.xIndex.sort((a, b) => {
			for (let i = 0; i < a[1].length; i++) {
				if (a[1][i] != b[1][i]) return a[1][i] - b[1][i];
			}
			return 0;
		});

		// update parent indices to match sorted index
		if (this._yParentBasesAxis > -1) {
			const indexMap = new Map<string, number>();
			let parentIndex: number;

			// the same item repeats under each item of the outer levels (e.g. the
			// statement rows of every company) - its parent is the one with the same outer levels
			const outerKeyOf = (item: AxisIndexItem, index: number) =>
				item[0].split(KEY_SEPARATOR).slice(0, this._yParentBasesAxis).concat(String(index)).join(KEY_SEPARATOR);

			// map indices on original result data indices
			this.yIndex.forEach((item, i) => {
				if (item[2] == this._yParentBasesAxis) {
					indexMap.set(outerKeyOf(item, item[1][this._yParentBasesAxis]), i);
				}
			});

			let parents: [number, number][]; // [index, level]
			let prevLevel: number, level: number;
			this.yIndex.forEach((item, i) => {
				parentIndex = item[4];

				if (item[2] == this._yParentBasesAxis) {
					// update parent index to new position based on original index
					item[4] = indexMap.get(outerKeyOf(item, parentIndex));
					parents = null;
					prevLevel = null;
				} else {
					// find the correct parent index based on level
					level = item[3];
					if (prevLevel == null) {
						parents = [[i - 1, -1]];
					} else if (prevLevel != null) {
						if (level > prevLevel) {
							parents.push([i - 1, prevLevel]);
						} else if (level < prevLevel) {
							while (parents.length > 0 && parents[parents.length - 1][1] >= level) {
								parents.pop();
							}
						}
					}
					item[4] = parents[parents.length - 1][0];
					prevLevel = level;
				}
			});
		}
	}

	/**
	 * Finance statements with EMPTY_ROWS keep every statement row in the axis
	 * result set while the data result only contains the rows with mapped or
	 * computed accounts (the server does not zero-fill the rest). Index the
	 * axis items with no data row too so the whole statement renders; their
	 * cells stay empty (no dataIndex record). The same goes for the selected
	 * scenarios (AB) with no data.
	 *
	 * Every dimension of the axis listing its empty items is filled - for every
	 * combination of the other levels that has data (e.g. each month above or
	 * below the scenarios). The dimensions are filled from the deepest one out,
	 * so with more of them the axis gets their full cartesian product.
	 */
	private fillMissingAxisItems(axes: AxisResult[], axisIndex: AxisIndexItem[], levelOffsets: number[], isYAxis: boolean) {
		const dimensionPositions = axes.map((_, level) => level).filter(level => axes[level].axisType == 'dimension');
		if (!dimensionPositions.length) return;

		const keys = new Set(axisIndex.map(item => item[0]));
		for (let p = dimensionPositions.length - 1; p >= 0; p--) {
			this.fillMissingAxisLevel(axes, dimensionPositions[p], axisIndex, keys, levelOffsets, isYAxis, dimensionPositions.length == 1);
		}
	}

	private fillMissingAxisLevel(axes: AxisResult[], axisPos: number, axisIndex: AxisIndexItem[], keys: Set<string>, levelOffsets: number[], isYAxis: boolean, singleDimension: boolean) {
		const axis = axes[axisPos];

		// the server marks the axes listing items with no data rows (EMPTY_ROWS
		// statements); older servers do not send the property - fall back to the
		// fin row dimension
		if ((axis.emptyItems ?? (axis.dimension == 'R' ? 'nulls' : 'none')) == 'none') return;

		const itemLevelOf = (index: number) => parseInt(axis.data[index][axis.columnIndex.level]);

		// combinations of the other levels to fill the missing items into, keyed
		// with the filled level left out: [key parts, indexes, axis level, item level without the filled item, parent index]
		const combinations = new Map<string, [string[], number[], number, number, number]>();
		const addCombination = (parts: string[], indexes: number[], level: number, baseLevel: number, parentIndex?: number) => {
			const key = parts.map((part, l) => l == axisPos ? '*' : part).join(KEY_SEPARATOR);
			if (!combinations.has(key)) combinations.set(key, [parts, indexes, level, baseLevel, parentIndex]);
		};

		for (const item of axisIndex) {
			const parts = item[0].split(KEY_SEPARATOR);
			if (parts[axisPos] === '' || item[1][axisPos] == null || item[1][axisPos] < 0) continue;
			addCombination(parts, item[1], item[2], item[3] == null ? null : item[3] - itemLevelOf(item[1][axisPos]), item[4]);
		}

		// a single dimension axis lists its items for every indicator even with no data at all
		if (singleDimension) {
			const axisLevel = axes.length - 1;
			const indicatorCount = axes.length > 1 ? this.indicators.length : 1;
			for (let j = 0; j < indicatorCount; j++) {
				addCombination(axes.map(() => 'I' + j), axes.map(() => j), axisLevel, (axes.length - 1) + (levelOffsets[axisLevel] || 0));
			}
		}

		for (const [parts, indexes, axisLevel, baseLevel, innerParentIndex] of combinations.values()) {
			// the parent index belongs to the deepest dimension with a value - keep
			// the one of the combination when the filled level is not that one
			const hasInnerDimension = parts.some((part, l) => l > axisPos && part !== '' && axes[l].axisType == 'dimension');

			for (let i = 0; i < axis.data.length; i++) {
				const dataRow = axis.data[i];
				const key = parts.map((part, l) => l == axisPos ? 'D' + dataRow[axis.columnIndex.idz] : part).join(KEY_SEPARATOR);
				if (keys.has(key)) continue;
				keys.add(key);

				const level = baseLevel + itemLevelOf(i);
				const itemLevel = baseLevel == null || isNaN(level) ? null : level;
				const itemIndexes = indexes.map((index, l) => l == axisPos ? i : index);

				if (isYAxis) {
					let parentIndex: number;
					if (hasInnerDimension) {
						parentIndex = innerParentIndex;
					} else {
						parentIndex = axis.columnIndex.parent > -1 ? parseInt(dataRow[axis.columnIndex.parent]) : null;
						if (parentIndex != null && parentIndex > -1) this._yParentBasesAxis = axisPos;
						else parentIndex = null;
					}

					this._yLevels = Math.max(this._yLevels, itemLevel);
					axisIndex.push([key, itemIndexes, axisLevel, itemLevel, parentIndex]);
				} else {
					this._xLevels = Math.max(this._xLevels, itemLevel);
					axisIndex.push([key, itemIndexes, axisLevel, itemLevel]);
				}
			}
		}
	}

	public addResultSet(resultSpec: ResultSpecs, data: string[][]) {
		if (resultSpec.tableType == 'axis') {
			const axisResult = resultSpec as AxisResult;
			axisResult.data = data;
			if (axisResult.axisType == 'indicators') this.indexIndicators(axisResult);
			else this.indexAxisResult(axisResult);
			(axisResult.axisIndex == 0 ? this.xAxis : this.yAxis).push(axisResult);
		} else if (resultSpec.tableType == 'data') {
			const dataResult = resultSpec as DataResult;
			dataResult.data = data,
			this._data = dataResult;
			this.indexDataResult(dataResult);
			this.indicators.forEach(indicator => {
				indicator.dataColumnIndex = findColumnIndex(dataResult.columns, indicator.dataColumn);
				indicator.unitDataColumnIndex = indicator.unitDataColumn ? findColumnIndex(dataResult.columns, indicator.unitDataColumn) : null;
			});
		} else if (resultSpec.tableType == 'theme') {
			// one-cell result set; empty data = the referenced style does not exist
			const index = Math.max(findColumnIndex(resultSpec.columns, 'THEME'), 0);
			const json = data[0]?.[index];
			if (json) {
				try {
					this._theme = JSON.parse(json);
				} catch (error) {
					console.warn('Invalid table theme JSON', error);
				}
			}
		} else if (resultSpec.tableType == 'comments') {
			const idzIndex = findColumnIndex(resultSpec.columns, 'IDZ');
			const rowKeyIndex = findColumnIndex(resultSpec.columns, 'ROW_KEY');
			const columnKeyIndex = findColumnIndex(resultSpec.columns, 'COLUMN_KEY');
			const textIndex = findColumnIndex(resultSpec.columns, 'TEXT');
			const whoCreatedIndex = findColumnIndex(resultSpec.columns, 'WHO_CREATED');
			const dateCreatedIndex = findColumnIndex(resultSpec.columns, 'DATE_CREATED');
			this._comments = data.map(row => ({
				idz: parseInt(row[idzIndex]),
				rowKey: row[rowKeyIndex],
				columnKey: row[columnKeyIndex],
				text: row[textIndex],
				whoCreated: row[whoCreatedIndex],
				dateCreated: row[dateCreatedIndex],
			}));
		}
	}

}
